import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth0 } from '@auth0/auth0-react';
import { IconTrash, IconCheck, IconClock, IconEye, IconFilter, IconChevronUp, IconChevronDown, IconDownload } from '@tabler/icons-react';
import { 
  Modal, 
  Button, 
  Table, 
  Group, 
  Text, 
  Box, 
  Loader, 
  Center, 
  TextInput, 
  Select, 
  MultiSelect, 
  Paper, 
  ActionIcon, 
  Collapse, 
  Stack,
  Badge,
  UnstyledButton
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  removeFile,
  getFilelist,
  notifyFileUpload,
  getPresignedURLs,
  getFileDownloadUrl,
  updateFileMetadata,
  batchDownloadFiles,
} from '../../services/removeFiles';
import { FileMetadata, PresignedURL, GetPresignedURLsResponse, FileListItem, FileMetadataUpdate } from '../../interfaces/types';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import DocumentViewer from '../../components/DocumentViewer';
import { safeFetchJson } from '../../utils/safeFetch';
import { safeAxiosPut } from '../../utils/safeAxios';
import useLocalStorage from '@/hooks/useLocalStorage';

type SortField = 'filename' | 'uploadedAt' | 'client' | 'isProcessed';
type SortDirection = 'asc' | 'desc';
type FilterField = 'filename' | 'client' | 'status';

const FileUpload: React.FC = () => {
  const [fileList, setFileList] = useState<FileListItem[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useLocalStorage<string>('fileUpload.searchTerm', '');
  const [filterField, setFilterField] = useLocalStorage<FilterField>('fileUpload.filterField', 'filename');
  const [statusFilter, setStatusFilter] = useLocalStorage<string[]>('fileUpload.statusFilter', []);

  const [showFilters, setShowFilters] = useLocalStorage<boolean>('fileUpload.showFilters', false);
  const [sortField, setSortField] = useLocalStorage<SortField>('fileUpload.sortField', 'uploadedAt');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('fileUpload.sortDirection', 'desc');
  const [fileToDelete, setFileToDelete] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Document viewer state
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerFilename, setViewerFilename] = useState('');
  const [viewerFileUrl, setViewerFileUrl] = useState<string | null>(null);
  const [viewerUploadMetadata, setViewerUploadMetadata] = useState<{ uploadedBy: string; uploadedAt: string } | null>(null);
  const [viewerFileMetadata, setViewerFileMetadata] = useState<FileListItem | null>(null);
  const [isLoadingViewer, setIsLoadingViewer] = useState(false);
  
  // Available options for filters
  const [availableLawyers, setAvailableLawyers] = useState<string[]>([]);
  const [isBatchDownloading, setIsBatchDownloading] = useState(false);
  
  const { getAccessTokenSilently, user } = useAuth0();
  const { t } = useTranslation();

  // Helper function to normalize boolean values (in case they come as strings from backend)
  const isWentThroughTrue = (value: any): boolean => {
    return value === true || String(value) === 'true';
  };

  const hasWentThroughValue = (value: any): boolean => {
    return value === true || value === false || String(value) === 'true' || String(value) === 'false';
  };

  // Enhanced search placeholder
  const getSearchPlaceholder = () => {
    switch (filterField) {
      case 'filename':
        return t('searchFiles');
      case 'client':
        return t('searchByClient');
      case 'status':
        return t('searchByStatus');
      default:
        return t('searchFiles');
    }
  };

  // Fetch available lawyers for filtering
  const fetchAvailableLawyers = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await safeFetchJson(`${import.meta.env.VITE_API_URL}/lawyerData/listLawyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableLawyers(data.map((lawyer: any) => lawyer.LawyerName));
    } catch (error) {
      console.error('Failed to fetch lawyers:', error);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchFileList = async () => {
      try {
        setLoading(true);
        const accessToken = await getAccessTokenSilently();
        const response = await getFilelist(accessToken);
        
        // Convert uploadedAt strings to Date objects for sorting/display
        const filesWithDates = response.files.map((file: FileListItem) => ({
          ...file,
          uploadedAt: file.uploadedAt,
        }));
        
        setFileList(filesWithDates);
        setFilteredFiles(filesWithDates);
      } catch (error) {
        console.error('Error fetching file list:', error);
        notifications.show({
          title: t('error'),
          message: t('errorFetchingAlert'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFileList();
    fetchAvailableLawyers();
  }, [getAccessTokenSilently, t, fetchAvailableLawyers]);

  // Enhanced filtering and sorting
  const filteredAndSortedFiles = useMemo(() => {
    // Debug: Log some files to see their wentThrough values

    
    let filtered = fileList.filter((file) => {
      // Text search based on selected filter field
      const searchLower = searchTerm.toLowerCase();
      let matchesText = false;

      switch (filterField) {
        case 'filename':
          matchesText = file.filename.toLowerCase().includes(searchLower);
          break;
        case 'client':
          matchesText = (file.client?.toLowerCase().includes(searchLower) ?? false);
          break;
        case 'status':
          const status = file.isProcessed ? 'processed' : 'pending';
          matchesText = t(status).toLowerCase().includes(searchLower);
          break;
        default:
          matchesText = file.filename.toLowerCase().includes(searchLower);
      }

      // Status filter
      const matchesStatus = statusFilter.length === 0 || statusFilter.includes(
        file.isProcessed ? 'processed' : 'pending'
      );

      // Lawyer filter
      

              return (searchTerm === '' || matchesText) && matchesStatus;
    });

    // Sort
    return filtered.sort((a, b) => {
      let aValue: string | Date | boolean;
      let bValue: string | Date | boolean;

      switch (sortField) {
        case 'filename':
          aValue = a.filename;
          bValue = b.filename;
          break;
        case 'uploadedAt':
          aValue = new Date(a.uploadedAt);
          bValue = new Date(b.uploadedAt);
          break;
        case 'client':
          aValue = a.client || '';
          bValue = b.client || '';
          break;
        case 'isProcessed':
          aValue = a.isProcessed;
          bValue = b.isProcessed;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [fileList, searchTerm, filterField, statusFilter, sortField, sortDirection, t]);

  // Update filteredFiles when filters change
  useEffect(() => {
    setFilteredFiles(filteredAndSortedFiles);
  }, [filteredAndSortedFiles]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    const icon = isActive ? (
      sortDirection === 'asc' ? (
        <IconChevronUp size={14} />
      ) : (
        <IconChevronDown size={14} />
      )
    ) : null;

    return (
      <Table.Th>
        <UnstyledButton onClick={() => handleSort(field)}>
          <Group gap={4} justify="space-between">
            <Text fw={500}>{children}</Text>
            {icon}
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const duplicateFiles: string[] = [];
        const uniqueFiles: File[] = [];

        const fileListNames = fileList.map((file) => file.filename);
        acceptedFiles.forEach((file) => {
          if (fileListNames.includes(file.name)) {
            duplicateFiles.push(file.name);
          } else {
            uniqueFiles.push(file);
          }
        });

        if (duplicateFiles.length > 0) {
          notifications.show({
            title: t('warning'),
            message: `The following files are already added and won't be uploaded: ${duplicateFiles.join(',\n ')}`,
            color: 'yellow',
            autoClose: 4000,
            position: 'top-right',
          });
        }

        if (uniqueFiles.length === 0) {
          notifications.show({
            title: t('info'),
            message: t('noNewFilesToUpload'),
            color: 'blue',
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }

        const accessToken = await getAccessTokenSilently();

        // Prepare file metadata for all accepted files
        const filesMetadata = uniqueFiles.map((file) => ({
          filename: file.name,
          filetype: file.type || 'application/octet-stream',
        }));

        // Get user info for upload tracking
        const uploadedBy = user?.email || user?.name || 'Unknown';

        // Request pre-signed URLs for all files
        const response: GetPresignedURLsResponse = await getPresignedURLs(
          accessToken,
          filesMetadata,
          'document-database',
          uploadedBy
        );
        const urls: PresignedURL[] = response.urls;

        // Map filenames to their corresponding URLs
        const urlMap = new Map(
          urls.map((item: { filename: string; url: string }) => [item.filename, item.url])
        );

        const successfullyUploadedFiles: string[] = [];
        const unsuccessfullyUploadedFiles: string[] = [];

        // Upload files concurrently using Promise.all
        const uploadPromises = uniqueFiles.map(async (file) => {
          const url = urlMap.get(file.name);
          if (!url) {
            console.error(`No pre-signed URL for file ${file.name}`);
            unsuccessfullyUploadedFiles.push(file.name);
            return;
          }
          try {
            await safeAxiosPut(url, file, {
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
              },
            });

            await notifyFileUpload(accessToken, file.name, 'add_and_embed_new_file');
            successfullyUploadedFiles.push(file.name);
          } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error);
            unsuccessfullyUploadedFiles.push(file.name);
          }
        });

        await Promise.all(uploadPromises);

        // Update state and UI with the list of successfully uploaded files
        setFileList((prevList) => {
          const newFiles = successfullyUploadedFiles.map((filename) => ({
            filename,
            isProcessed: false,
            uploadedBy,
            uploadedAt: new Date().toISOString(),
          }));
          
          const updatedList = [...prevList, ...newFiles].sort((a, b) => 
            a.filename.localeCompare(b.filename)
          );
          
          setSearchTerm('');
          return updatedList;
        });

        // Provide user feedback
        if (successfullyUploadedFiles.length > 0) {
          notifications.show({
            title: t('success'),
            message: `${t('successfulFileUpload')} ${successfullyUploadedFiles.join(',\n')}`,
            color: 'green',
            autoClose: 3000,
            position: 'top-right',
          });
        }
        if (unsuccessfullyUploadedFiles.length > 0) {
          notifications.show({
            title: t('error'),
            message: `${t('unsuccessfulFileUpload')} ${unsuccessfullyUploadedFiles.join(',\n')}`,
            color: 'red',
            autoClose: 5000,
            position: 'top-right',
          });
        }
      } catch (error) {
        console.error('Error during file upload:', error);
        notifications.show({
          title: t('error'),
          message: t('errorUploadingFiles'),
          color: 'red',
          autoClose: 3000,
          position: 'top-right',
        });
      }
    },
    [fileList, getAccessTokenSilently, t, user]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'], // Accept PDF files
      'text/plain': ['.txt'], // Accept text files
      'application/msword': ['.doc'], // Accept .doc files
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'], // Accept .docx files
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'], // Accept .pptx files
    },
  });

  const handleDeleteClick = (filename: string) => {
    setFileToDelete(filename);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    const filename = fileToDelete;
    const remove = async () => {
      setFileList((prevList) => {
        const updatedList = prevList.filter((file) => file.filename !== fileToDelete);
        return updatedList;
      });
      const accessToken = await getAccessTokenSilently();
      await removeFile(accessToken, filename);
    };
    remove();
    setIsModalOpen(false);
  };

  const formatUploadDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(); // Only show date, no time
  };

  const handleViewClick = async (filename: string) => {
    setViewerFilename(filename);
    setIsViewerOpen(true);
    setIsLoadingViewer(true);
    setViewerFileUrl(null);
    setViewerUploadMetadata(null);
    setViewerFileMetadata(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await getFileDownloadUrl(accessToken, filename);
      setViewerFileUrl(response.url);
      setViewerUploadMetadata(response.uploadMetadata || null);
      
      // The DocumentViewer will use uploadMetadata (fresh DynamoDB data) directly
      const currentFileMetadata = fileList.find(file => file.filename === filename);
      setViewerFileMetadata(currentFileMetadata || null);
    } catch (error) {
      console.error('Error getting file URL:', error);
      notifications.show({
        title: t('error'),
        message: t('errorLoadingFile'),
        color: 'red',
        autoClose: 3000,
        position: 'top-right',
      });
    } finally {
      setIsLoadingViewer(false);
    }
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setViewerFilename('');
    setViewerFileUrl(null);
    setViewerUploadMetadata(null);
    setViewerFileMetadata(null);
    setIsLoadingViewer(false);
  };

  const handleMetadataUpdate = async (metadata: FileMetadataUpdate) => {
    try {
      const accessToken = await getAccessTokenSilently();
      await updateFileMetadata(accessToken, metadata);
      
      // Update local file list
      setFileList(prev => prev.map(file => 
        file.filename === metadata.filename 
          ? { ...file, ...metadata }
          : file
      ));
      
      // Update viewer metadata
      if (viewerFileMetadata && viewerFileMetadata.filename === metadata.filename) {
        setViewerFileMetadata({ ...viewerFileMetadata, ...metadata });
      }
      
      notifications.show({
        title: t('success'),
        message: t('metadataUpdatedSuccessfully'),
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating metadata:', error);
      throw error; // Re-throw for DocumentViewer to handle
    }
  };



  const handleBatchDownload = async () => {
    setIsBatchDownloading(true);
    try {
      const accessToken = await getAccessTokenSilently();
      await batchDownloadFiles(accessToken);
    } catch (error: any) {
      console.error('Batch download error:', error);
      const isNotReady = error?.message?.includes('404');
      notifications.show({
        title: t('error'),
        message: isNotReady ? t('zipNotReady') : t('batchDownloadFailed'),
        color: isNotReady ? 'yellow' : 'red',
        autoClose: 4000,
        position: 'top-right',
      });
    } finally {
      setIsBatchDownloading(false);
    }
  };

  return (
    <Box miw={700} w="90%" maw={1200}>
      <div
        {...getRootProps()}
        style={{ border: '2px dashed #0087F7', padding: '20px', textAlign: 'center' }}
      >
        <input {...getInputProps()} />
        <p>{t('dragDrop')}</p>
      </div>

      <Box mt="lg">
        <Group justify="space-between" align="center" mb="md">
          <h3 style={{ margin: 0 }}>{t('uploadedFiles')}</h3>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="light"
            color="blue"
            loading={isBatchDownloading}
            onClick={handleBatchDownload}
          >
            {t('downloadAllFiles')}
          </Button>
        </Group>
        
        {/* Enhanced Search and Filter Section */}
        <Paper p="md" mb="md" withBorder>
          <Group justify="space-between" align="end" w="100%" mb="md">
            <Group gap="md" align="end">
              <Select
                label={t('searchIn')}
                data={[
                  { value: 'filename', label: t('filename') },
                  { value: 'client', label: t('client') },
                  { value: 'status', label: t('status') },
                ]}
                value={filterField}
                onChange={(value) => setFilterField(value as FilterField)}
                w={160}
              />
              <TextInput
                placeholder={getSearchPlaceholder()}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                w={300}
              />
              <ActionIcon
                variant="light"
                onClick={() => setShowFilters(!showFilters)}
                title={t('advancedFilters')}
                size="lg"
              >
                <IconFilter size={16} />
              </ActionIcon>
            </Group>
          </Group>

          <Collapse in={showFilters}>
            <Stack gap="md">
              <Group grow>
                <MultiSelect
                  label={t('filterByStatus')}
                  placeholder={t('selectStatus')}
                  data={[
                    { value: 'processed', label: t('processed') },
                    { value: 'pending', label: t('pending') },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  clearable
                />

              </Group>
            </Stack>
          </Collapse>
        </Paper>

        {loading ? (
          <Center style={{ height: '20vh' }}>
            <Loader size="lg" />
          </Center>
        ) : filteredFiles.length > 0 ? (
          <Table.ScrollContainer minWidth={800} style={{ margin: '0 1rem' }}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <SortableHeader field="filename">{t('filename')}</SortableHeader>
                  <SortableHeader field="client">{t('client')}</SortableHeader>
                  <SortableHeader field="isProcessed">{t('status')}</SortableHeader>
                  <SortableHeader field="uploadedAt">{t('uploadedAt')}</SortableHeader>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredFiles.map((file) => (
                  <Table.Tr key={file.filename}>
                    <Table.Td>
                      <Text size="sm" style={{ wordBreak: 'break-all' }}>
                        {file.filename}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{file.client || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {file.isProcessed ? (
                          <IconCheck size={16} color="green" />
                        ) : (
                          <IconClock size={16} color="orange" />
                        )}
                        <Text size="sm">{file.isProcessed ? t('processed') : t('pending')}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatUploadDate(file.uploadedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <IconEye
                          style={{ cursor: 'pointer', color: 'blue' }}
                          onClick={() => handleViewClick(file.filename)}
                        />
                        <IconTrash
                          style={{ cursor: 'pointer', color: 'red' }}
                          onClick={() => handleDeleteClick(file.filename)}
                        />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <Center style={{ padding: '2rem' }}>
                            <Text c="dimmed">{searchTerm || statusFilter.length > 0 ? t('noMatchFiles') : t('noFilesFound')}</Text>
          </Center>
        )}
      </Box>

      {/* Modal for delete confirmation */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('confirmDeletion')}
      >
        <p>
          {t('deleteFileConfirmation')} {fileToDelete}?
        </p>
        <Button color="red" onClick={confirmDelete}>
          {t('confirm')}
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsModalOpen(false)}
          style={{ marginLeft: '10px' }}
        >
          {t('cancel')}
        </Button>
      </Modal>

      {/* Enhanced Document Viewer Modal */}
      <DocumentViewer
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        filename={viewerFilename}
        fileUrl={viewerFileUrl}
        uploadMetadata={viewerUploadMetadata}
        fileMetadata={viewerFileMetadata}
        isLoading={isLoadingViewer}
        onMetadataUpdate={handleMetadataUpdate}
      />
    </Box>
  );
};

export default FileUpload;
